import { ConnectProto } from "@racoon/api";
import { getJobPhotos } from "~/shared/utils/jobPhotos";

/**
 * How complete a job's photo report is, as the "Отчет" column shows it:
 * - `notFinished` — the job has not reached a final status, so no photo is owed yet
 * - `missing` — finished, but not a single photo it should have
 * - `partial` — completed with only "before" or only "after" photos
 * - `complete` — every photo group the outcome calls for is there
 */
export type JobReportState = "notFinished" | "missing" | "partial" | "complete";

const FINISHED_STATUSES: ReadonlySet<ConnectProto.JobStatus> = new Set([
  ConnectProto.JobStatus.COMPLETED,
  ConnectProto.JobStatus.FORCE_COMPLETED,
  ConnectProto.JobStatus.NOT_COMPLETED,
]);

/** Completed (normally or forcibly) or failed ("Невывоз") — the job will not change any more. */
export function isJobFinished(status: ConnectProto.JobStatus | undefined): boolean {
  return status != null && FINISHED_STATUSES.has(status);
}

export function getJobReportState(job: {
  status?: ConnectProto.JobStatus;
  fileReports?: any[];
}): JobReportState {
  if (!isJobFinished(job.status)) return "notFinished";

  const { beforePhotos, afterPhotos, failureReasonPhotos } = getJobPhotos(job);

  // A failed pickup is proven by the failure-reason photo alone; nothing was
  // serviced, so "before"/"after" photos are not expected.
  if (job.status === ConnectProto.JobStatus.NOT_COMPLETED) {
    return failureReasonPhotos.length > 0 ? "complete" : "missing";
  }

  const hasBefore = beforePhotos.length > 0;
  const hasAfter = afterPhotos.length > 0;
  if (hasBefore && hasAfter) return "complete";
  if (hasBefore || hasAfter) return "partial";
  return "missing";
}
